import React, { forwardRef, useEffect, useRef, useState } from "react";

import EventNotes from "../EventNotes";
import { createGuidId } from "../../../utils/teams-dragger";
import Header from "../Header";
import ChangeEventType from "../ChangeEventType";
import SelectChangePlayers from "../SelectChangePlayers";
import SelectChangeStaff from "../SelectChangeStaff";
import TypeOfEventType from "../TypeOfEventType";
import ConfirmButton from "../ConfirmButton";
import SuspensionTime from "../SuspensionTime";
import Submit from "../HandleSubmit";

const POST_TYPE_MAP = {
    GELB: 10,
    GRÜN: 11,
    ROT: 12,
    "GELB ROT": 37,
};

const QUARTER_MAP = {
    Q1: 1,
    Q2: 2,
    Q3: 3,
    Q4: 4,
};

export const formatData = ({
    eventsToShow,
    matchId,
    matchData,
    setMatchData,
    formatApiData,
    homePlayers,
    awayPlayers,
    setEventsToShow,
    sides,
}) => {
    const event = eventsToShow.at(-1);
    if (!event) return;

    const {
        sideSelected,
        playerSelected,
        type,
        timestamp,
        quarterSelected,
        suspensionTime,
        eventNotes,
    } = event;

    const minute = Math.ceil(timestamp / 60);

    const cardEvent = {
        type: "Cards",
        postType: POST_TYPE_MAP[type?.name],
        match: matchId,
        team: sideSelected?.id,
        player: playerSelected?.id,
        minute,
        orderIndex: matchData.matchPosts?.length,
        quarter: QUARTER_MAP[quarterSelected],
        homeGoals: matchData.homeGoals,
        awayGoals: matchData.awayGoals,
        isLive: true,
        suspensionMinutes: suspensionTime?.minutes ?? null,
        ...(eventNotes?.trim() && { additionalData: eventNotes.trim() }),
        ...(playerSelected?.staff && { staff: true }),
    };

    const update = {
        matchPosts: [...matchData.matchPosts, cardEvent],
    };

    Submit(matchId, matchData, update, setMatchData)
        .then((data) =>
            formatApiData(
                data,
                sides,
                setEventsToShow,
                ...homePlayers,
                ...awayPlayers
            )
        )
        .catch((e) => console.error(e.message));
};

const AddCardEvent = forwardRef(
    (
        {
            setIsModalOpen,
            EVENT_TYPES,
            setEventsToShow,
            eventDetailsToAdd,
            setEventDetailsToAdd,
            setSides,
            goals,
            setGoals,
            isEditing,
            setIsEditing,
            REASONS_FOR_CARDS,
            TIME_SUSPENSION_FOR_CARDS,
            sides,
            eventsToShow,
            matchId,
            matchData,
            setMatchData,
            formatApiData,
            homePlayers,
            awayPlayers,
            homeStaff,
            awayStaff,
            updateStaffHandler,
        },
        ref
    ) => {
        const [step, setStep] = useState("0");
        const isFirstRun = useRef(true);
        const initialEvent = useRef(eventDetailsToAdd);
        useEffect(() => {
            if (isFirstRun.current) {
                isFirstRun.current = false;
                return;
            }
            formatData({
                eventsToShow,
                matchId,
                matchData,
                setMatchData,
                formatApiData,
                homePlayers,
                awayPlayers,
                setEventsToShow,
                sides,
            });
        }, [eventsToShow]);

        const showDoneButton =
            (eventDetailsToAdd?.playerSelected ||
                eventDetailsToAdd?.playerSelected === false) &&
            eventDetailsToAdd?.type &&
            eventDetailsToAdd?.sideSelected &&
            eventDetailsToAdd?.sideNotSelected &&
            (eventDetailsToAdd?.type?.name === "GELB" ||
            eventDetailsToAdd?.type?.name === "GRÜN"
                ? eventDetailsToAdd?.suspensionTime
                : true);

        return (
            <div
                ref={ref}
                style={{
                    backgroundColor: "#E5E5E5",
                }}
            >
                <Header
                    eventDetails={eventDetailsToAdd}
                    setEventDetails={setEventDetailsToAdd}
                    setModalOpen={setIsModalOpen}
                    setSides={setSides}
                    goals={goals}
                    setGoals={setGoals}
                    isEditing={isEditing}
                    matchData={matchData}
                />
                {[
                    "0",
                    "0,1",
                    "0,1,2",
                    "0,1,2,3",
                    "0,1,2,4",
                    "0,1,2,3,4",
                ].includes(step) && (
                    <div>
                        <SelectChangePlayers
                            eventDetails={eventDetailsToAdd}
                            setEventDetails={setEventDetailsToAdd}
                            changeCallback={() => {
                                setStep("0");
                                setEventDetailsToAdd((data) => ({
                                    ...data,
                                    playerSelected: null,
                                    suspensionTime: null,
                                }));
                            }}
                            confirmCallback={() => {
                                setStep("0,1,2");
                            }}
                            skipCallback={() => {
                                setStep("0,0.5");
                            }}
                            initialEvent={initialEvent}
                        />
                        <SelectChangeStaff
                            skipCallback={() => {
                                setStep("0,0.5");
                            }}
                            confirmCallback={() => {
                                setStep("0,1,2");
                            }}
                            setEventDetails={setEventDetailsToAdd}
                            initialEvent={initialEvent}
                            eventDetails={eventDetailsToAdd}
                            sideSelected={eventDetailsToAdd?.sideSelected?.side}
                            selectedColor={
                                eventDetailsToAdd?.sideSelected?.teamColor
                            }
                            homeStaff={homeStaff}
                            awayStaff={awayStaff}
                        />
                    </div>
                )}
                {[
                    "0,1",
                    "0,0.5",
                    "0,0.5,2",
                    "0,1,2",
                    "0,0.5,2,3",
                    "0,1,2,3",
                    "0,0.5,2,4",
                    "0,1,2,4",
                    "0,1,2,3,4",
                    "0,0.5,2,3,4",
                ].includes(step) && (
                    <ChangeEventType
                        EVENT_TYPES={EVENT_TYPES}
                        eventDetails={eventDetailsToAdd}
                        setEventDetails={setEventDetailsToAdd}
                        changeCallback={() => {
                            if (
                                ["GELB"]?.includes(
                                    eventDetailsToAdd?.type?.name
                                )
                            ) {
                                if (
                                    eventDetailsToAdd?.typeOfEventTypeSelected
                                ) {
                                    setStep((data) =>
                                        data?.split(",").slice(0, -3).join(",")
                                    );
                                } else if (
                                    !eventDetailsToAdd?.typeOfEventTypeSelected
                                ) {
                                    setStep((data) =>
                                        data?.split(",").slice(0, -2).join(",")
                                    );
                                } else {
                                    setStep((data) =>
                                        data?.split(",").slice(0, -1).join(",")
                                    );
                                }
                            } else {
                                if (
                                    eventDetailsToAdd?.typeOfEventTypeSelected
                                ) {
                                    setStep((data) =>
                                        data?.split(",").slice(0, -2).join(",")
                                    );
                                } else if (
                                    !eventDetailsToAdd?.typeOfEventTypeSelected
                                ) {
                                    setStep((data) =>
                                        data?.split(",").slice(0, -1).join(",")
                                    );
                                } else {
                                    setStep((data) =>
                                        data?.split(",").slice(0, -1).join(",")
                                    );
                                }
                            }
                            setEventDetailsToAdd((data) => ({
                                ...data,
                                type: null,
                                suspensionTime: null,
                                typeOfEventTypeSelected: null,
                            }));
                        }}
                        confirmCallback={(et) => {
                            setStep((data) => data + ",2");
                            et?.name === "GRÜN" &&
                                setEventDetailsToAdd((data) => ({
                                    ...data,
                                    type: et,
                                    suspensionTime: {
                                        id: 0,
                                        name: "2 Mins",
                                        backgroundColor: "white",
                                        color: "black",
                                        borderColor: "black",
                                    },
                                }));
                        }}
                        preventChange={true}
                    />
                )}

                {["GELB", "GRÜN"]?.includes(eventDetailsToAdd?.type?.name) && (
                    <>
                        {[
                            "0,1,2",
                            "0,0.5,2",
                            "0,0.5,2,5",
                            "0,1,2,3",
                            "0,0.5,2,3",
                            "0,0.5,2,4",
                            "0,1,2,4",
                            "0,1,2,3,4",
                            "0,0.5,2,3,4",
                        ].includes(step) && (
                            <SuspensionTime
                                GOAL_TYPES={TIME_SUSPENSION_FOR_CARDS}
                                eventDetails={eventDetailsToAdd}
                                setEventDetails={setEventDetailsToAdd}
                                changeCallback={() => {
                                    if (
                                        eventDetailsToAdd?.typeOfEventTypeSelected
                                    ) {
                                        setStep((data) =>
                                            data
                                                ?.split(",")
                                                .slice(0, -2)
                                                .join(",")
                                        );
                                    } else if (
                                        !eventDetailsToAdd?.typeOfEventTypeSelected
                                    ) {
                                        setStep((data) =>
                                            data
                                                ?.split(",")
                                                .slice(0, -1)
                                                .join(",")
                                        );
                                    } else {
                                        setStep((data) =>
                                            data
                                                ?.split(",")
                                                .slice(0, -1)
                                                .join(",")
                                        );
                                    }

                                    setEventDetailsToAdd((data) => ({
                                        ...data,
                                        suspensionTime: null,
                                        typeOfEventTypeSelected: null,
                                    }));
                                }}
                                confirmCallback={() => {
                                    setStep((data) => data + ",3");
                                }}
                            />
                        )}
                    </>
                )}

                {[
                    !["GELB", "GRÜN"].includes(eventDetailsToAdd?.type?.name)
                        ? "0,1,2"
                        : "",
                    !["GELB", "GRÜN"].includes(eventDetailsToAdd?.type?.name)
                        ? "0,0.5,2"
                        : "",
                    "0,1,2,3",
                    "0,0.5,2,3",
                    "0,0.5,2,4",
                    "0,1,2,4",
                    "0,1,2,3,4",
                    "0,0.5,2,3,4",
                ].includes(step) && (
                    <TypeOfEventType
                        GOAL_TYPES={REASONS_FOR_CARDS}
                        eventDetails={eventDetailsToAdd}
                        setEventDetails={setEventDetailsToAdd}
                        changeCallback={() => {
                            setStep((data) =>
                                data?.split(",").slice(0, -1).join(",")
                            );
                            setEventDetailsToAdd((data) => ({
                                ...data,
                                typeOfEventTypeSelected: null,
                            }));
                        }}
                        confirmCallback={() => {
                            setStep((data) => data + ",4");
                        }}
                    />
                )}
                {[
                    !["GELB", "GRÜN"].includes(eventDetailsToAdd?.type?.name)
                        ? "0,1,2"
                        : "",
                    !["GELB", "GRÜN"].includes(eventDetailsToAdd?.type?.name)
                        ? "0,0.5,2"
                        : "",
                    "0,1,2,3",
                    "0,0.5,2,3",
                    "0,0.5,2,5",
                    "0,1,2,5",
                    "0,1,2,4",
                    "0,0.5,2,4",
                    "0,1,2,3,4",
                    "0,0.5,2,3,4",
                ].includes(step) && (
                    <>
                        <EventNotes
                            textAreaValue={eventDetailsToAdd?.eventNotes}
                            showConfirmCancelButtons={
                                !!eventDetailsToAdd?.eventNotes?.length
                            }
                            confirmButtonAction={() => {}}
                            cancelButtonAction={() => {
                                setEventDetailsToAdd((data) => ({
                                    ...data,
                                    eventNotes: "",
                                }));
                            }}
                            onChange={(e) => {
                                setEventDetailsToAdd((data) => ({
                                    ...data,
                                    eventNotes: e.target.value,
                                }));
                            }}
                        />
                        {showDoneButton && (
                            <ConfirmButton
                                action={() => {
                                    if (
                                        !eventDetailsToAdd ||
                                        !eventDetailsToAdd?.type ||
                                        !eventDetailsToAdd?.type?.name
                                    ) {
                                        return;
                                    }
                                    const {
                                        type: { name },
                                        playerSelected,
                                        sideSelected,
                                    } = eventDetailsToAdd;
                                    if (playerSelected.staff) {
                                        updateStaffHandler(
                                            playerSelected,
                                            sideSelected.side,
                                            name,
                                            false
                                        );
                                    } else {
                                        const updatePlayerCards = (card) => {
                                            setSides((data) => ({
                                                ...data,
                                                [sideSelected.side]: {
                                                    ...data[sideSelected.side],
                                                    players: data[
                                                        sideSelected.side
                                                    ]?.players?.map((p) =>
                                                        p.playerName ===
                                                        playerSelected.playerName
                                                            ? {
                                                                  ...p,
                                                                  cards: [card],
                                                              }
                                                            : p
                                                    ),
                                                },
                                            }));
                                        };
                                        switch (name) {
                                            case "GELB":
                                            case "GELB ROT":
                                            case "ROT":
                                            case "GRÜN":
                                                updatePlayerCards(name);
                                                break;
                                        }
                                    }
                                    setEventsToShow((data) => {
                                        return [
                                            ...data,
                                            {
                                                id: createGuidId(),
                                                ...eventDetailsToAdd,
                                            },
                                        ];
                                    });
                                    setIsEditing(false);
                                    setIsModalOpen(false);
                                }}
                            />
                        )}
                    </>
                )}
            </div>
        );
    }
);

export default AddCardEvent;
